import React from 'react';
import { Link } from 'react-router-dom';
import { useT, useLang } from '../context/LanguageContext';
import { useCurrency } from '../context/CurrencyContext';
import Price from '../components/payments/Price';
import RazorpayCheckoutButton from '../components/payments/RazorpayCheckoutButton';
import { Check, Sparkles, Trophy, Users, Mic, ShieldCheck, RefreshCw } from 'lucide-react';

/**
 * Product catalog — all prices in EUR (single source of truth).
 * Add new products here; they'll appear automatically wherever the pricing grid is used.
 */
export const PRODUCTS = [
  {
    id: 'teacher_hour',
    kind: 'one-time',
    priceEur: 20,
    suffix: { fr: ' / heure', en: ' / hour' },
    eyebrow: { fr: 'À la carte', en: 'Pay as you go' },
    title: { fr: 'Réserver un professeur', en: 'Book a Teacher' },
    desc: {
      fr: "Une heure de cours particulier avec un(e) professeur(e) natif(ve). Conversation, prononciation, préparation d'examens.",
      en: 'One hour of private tutoring with a native French teacher. Conversation, pronunciation, exam preparation.',
    },
    features: [
      { fr: 'Professeur natif(ve) certifié(e)', en: 'Certified native teacher' },
      { fr: 'Cours 100% personnalisé', en: '100% personalised session' },
      { fr: 'Support de cours envoyé après', en: 'Session notes sent afterwards' },
      { fr: 'Annulation gratuite jusqu\'à 24h avant', en: 'Free cancellation up to 24h before' },
    ],
    accent: '#1A2F4A',
    icon: Mic,
    testid: 'product-teacher-hour',
  },
  {
    id: 'membership',
    kind: 'one-time-membership',
    priceEur: 50,
    suffix: { fr: ' · paiement unique', en: ' · one-time' },
    eyebrow: { fr: 'Recommandé', en: 'Recommended' },
    title: { fr: 'Adhésion Selora', en: 'Selora Membership' },
    desc: {
      fr: "Accès complet à Speaking Lab, Lire le Français, Les Mots en Voyage, et à toutes les histoires premium.",
      en: 'Full access to Speaking Lab, Lire le Français, Les Mots en Voyage, and all premium stories.',
    },
    features: [
      { fr: 'Tous les modules A1 → C1', en: 'All modules A1 → C1' },
      { fr: 'Nouvelles histoires chaque mois', en: 'New stories every month' },
      { fr: '2 sessions Club Francophone incluses', en: '2 Club Francophone sessions included' },
      { fr: 'Suivi de progression personnalisé', en: 'Personal progress tracking' },
    ],
    accent: '#D4AF37',
    icon: Trophy,
    highlight: true,
    testid: 'product-membership',
  },
  {
    id: 'club',
    kind: 'club',
    priceEur: 5,
    suffix: { fr: ' / mois', en: ' / month' },
    eyebrow: { fr: 'Communauté', en: 'Community' },
    title: { fr: 'Le Club Francophone', en: 'Le Club Francophone' },
    desc: {
      fr: "Rejoignez les conversations en direct hebdomadaires. Résiliable à tout moment depuis votre espace.",
      en: 'Join the weekly live conversations. Cancel anytime from your dashboard.',
    },
    features: [
      { fr: 'Sessions live hebdomadaires', en: 'Weekly live sessions' },
      { fr: 'Petits groupes (max 8 personnes)', en: 'Small groups (max 8 people)' },
      { fr: 'Thèmes culturels & actualité', en: 'Cultural & current-affairs themes' },
      { fr: 'Annulation à tout moment', en: 'Cancel anytime' },
    ],
    accent: '#6B4E37',
    icon: Users,
    testid: 'product-club',
  },
];

export default function Pricing() {
  const t = useT();
  const { lang } = useLang();
  const { currency, ratesError } = useCurrency();

  return (
    <div className="min-h-screen bg-[#FAF7F0]" data-testid="pricing-page">
      {/* HERO */}
      <section className="pt-16 pb-10">
        <div className="max-w-[960px] mx-auto px-6 text-center">
          <p className="text-[10px] text-[#D4AF37] uppercase tracking-[0.3em] font-bold mb-3">
            {t({ fr: 'Tarifs', en: 'Pricing' })}
          </p>
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#1A2F4A] mb-5 leading-[1.05]"
            style={{ fontFamily: "'Playfair Display', serif" }}
            data-testid="pricing-title">
            {t({ fr: 'Investissez dans votre français', en: 'Invest in your French' })}
          </h1>
          <p className="text-base sm:text-lg text-[#6B4E37] italic max-w-2xl mx-auto" style={{ fontFamily: "'Playfair Display', serif" }}>
            {t({
              fr: 'Trois formules, une seule ambition : vous faire parler avec confiance.',
              en: 'Three plans, one ambition: to make you speak with confidence.',
            })}
          </p>
          <div className="mt-6 inline-flex items-center gap-2 bg-white border border-[#E8E4DC] rounded-full px-4 py-2 text-[12px] text-[#6B4E37]" data-testid="pricing-currency-badge">
            <RefreshCw className="w-3 h-3 text-[#D4AF37]" />
            {t({ fr: 'Prix affichés en', en: 'Prices displayed in' })} <strong className="text-[#1A2F4A]">{currency}</strong>
            {ratesError && (
              <span className="text-red-600">· {t({ fr: 'taux hors ligne', en: 'offline rate' })}</span>
            )}
          </div>
        </div>
      </section>

      {/* PRODUCT GRID */}
      <section className="pb-14">
        <div className="max-w-[1200px] mx-auto px-6 grid md:grid-cols-3 gap-6">
          {PRODUCTS.map((p) => {
            const Icon = p.icon;
            return (
              <div
                key={p.id}
                data-testid={p.testid}
                className={`relative bg-white rounded-2xl px-7 py-8 border-2 flex flex-col ${p.highlight ? 'border-[#D4AF37] shadow-xl' : 'border-[#E8E4DC] hover:border-[#D4AF37]/70 transition-colors'}`}>
                {p.highlight && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#D4AF37] text-[#1A2F4A] text-[10px] font-bold px-3 py-1 rounded-full tracking-widest">
                    {t(p.eyebrow)}
                  </div>
                )}
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-11 h-11 rounded-full bg-[#FAF7F0] border border-[#D4AF37]/40 flex items-center justify-center flex-shrink-0">
                    <Icon className="w-5 h-5" style={{ color: p.accent }} />
                  </div>
                  <div>
                    {!p.highlight && (
                      <p className="text-[9px] text-[#D4AF37] uppercase tracking-[0.25em] font-bold">{t(p.eyebrow)}</p>
                    )}
                    <h3 className="text-lg font-bold text-[#1A2F4A]" style={{ fontFamily: "'Playfair Display', serif" }}>
                      {t(p.title)}
                    </h3>
                  </div>
                </div>

                <div className="mb-4">
                  <Price basePriceEUR={p.priceEur} suffix={t(p.suffix)} size="lg" showBase testid={`${p.testid}-price`} />
                </div>

                <p className="text-[13px] text-[#6B4E37] leading-relaxed mb-5">{t(p.desc)}</p>

                <ul className="space-y-2 mb-6 flex-1">
                  {p.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-[13px] text-[#1A2F4A]/90">
                      <Check className="w-4 h-4 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                      <span>{t(f)}</span>
                    </li>
                  ))}
                </ul>

                {p.kind === 'one-time' && (
                  <RazorpayCheckoutButton
                    amountEur={p.priceEur}
                    product={p.id}
                    label={typeof p.title === 'object' ? p.title[lang] || p.title.fr : p.title}
                    mode="one-time"
                    className="px-6 py-3 text-sm w-full"
                    dataTestid={`${p.testid}-pay`}>
                    {t({ fr: 'Réserver maintenant', en: 'Book Now' })}
                  </RazorpayCheckoutButton>
                )}

                {p.kind === 'one-time-membership' && (
                  <RazorpayCheckoutButton
                    amountEur={p.priceEur}
                    product={p.id}
                    label={typeof p.title === 'object' ? p.title[lang] || p.title.fr : p.title}
                    mode="one-time"
                    className="px-6 py-3 text-sm w-full"
                    dataTestid={`${p.testid}-pay`}>
                    {t({ fr: 'Payer maintenant', en: 'Pay Now' })}
                  </RazorpayCheckoutButton>
                )}

                {p.kind === 'club' && (
                  <div className="space-y-2">
                    <RazorpayCheckoutButton
                      amountEur={p.priceEur}
                      product={p.id}
                      label={typeof p.title === 'object' ? p.title[lang] || p.title.fr : p.title}
                      mode="subscription"
                      planKind="club"
                      className="px-6 py-3 text-sm w-full"
                      dataTestid={`${p.testid}-subscribe`}>
                      {t({ fr: "S'abonner mensuellement", en: 'Subscribe Monthly' })}
                    </RazorpayCheckoutButton>
                    <RazorpayCheckoutButton
                      amountEur={p.priceEur}
                      product={p.id}
                      label={typeof p.title === 'object' ? p.title[lang] || p.title.fr : p.title}
                      mode="one-time"
                      className="px-6 py-2 text-xs w-full bg-white border border-[#E8E4DC] text-[#1A2F4A] hover:bg-[#FAF7F0]"
                      dataTestid={`${p.testid}-paymonth`}>
                      {t({ fr: 'Payer un seul mois', en: 'Pay one month only' })}
                    </RazorpayCheckoutButton>
                    <p className="text-[10px] text-[#6B4E37] italic text-center" data-testid={`${p.testid}-cancel-note`}>
                      {t({ fr: 'Annulable à tout moment depuis votre espace.', en: 'Cancel anytime from your dashboard.' })}
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* TRUST */}
      <section className="pb-20">
        <div className="max-w-[860px] mx-auto px-6">
          <div className="bg-white border border-[#E8E4DC] rounded-2xl px-6 py-5 flex flex-col sm:flex-row items-center gap-4">
            <div className="w-11 h-11 rounded-full bg-[#FAF7F0] border border-[#D4AF37]/40 flex items-center justify-center flex-shrink-0">
              <ShieldCheck className="w-5 h-5 text-[#D4AF37]" />
            </div>
            <div className="text-[13px] text-[#6B4E37] leading-relaxed">
              {t({
                fr: 'Paiements sécurisés par Razorpay · Cryptés de bout en bout · Vous ne payez que ce que vous voyez à l\'écran.',
                en: 'Payments secured by Razorpay · End-to-end encrypted · You only pay what you see on screen.',
              })}
            </div>
            <Link
              to="/dashboard"
              className="ml-auto text-[12px] font-semibold text-[#1A2F4A] hover:text-[#D4AF37] transition-colors flex items-center gap-1 whitespace-nowrap"
              data-testid="pricing-dashboard-link">
              <Sparkles className="w-3 h-3 text-[#D4AF37]" />
              {t({ fr: 'Voir mon compte', en: 'Go to my account' })}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
